import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  TextField,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import { Email as EmailIcon, Send as SendIcon, ArrowBack } from "@mui/icons-material";
import { useRequestPasswordReset } from "../../hooks/auth";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH, RESET_PASSWORD_ROUTE_PATH } from "../../utils/constants/AppConstants";

const ForgotPasswordPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const { mutate: requestPasswordReset, isPending: isSubmitting } = useRequestPasswordReset();

  const isValidEmail = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${RESET_PASSWORD_ROUTE_PATH}`;
    const callbackUrl = new URL(path, window.location.origin).toString();
    
    requestPasswordReset(
      { email: email.trim(), callbackUrl },
      {
        onSuccess: () => {
          setMessage("If an account exists for this email, a reset link has been sent.");
          const endsAt = Date.now() + 60_000; // 60s cooldown to avoid spam
          setCooldownEndsAt(endsAt);
          setSecondsLeft(60);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Could not send reset instructions. Please try again.");
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!cooldownEndsAt) return;
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setCooldownEndsAt(null);
        clearInterval(interval);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const isCooldown = cooldownEndsAt !== null;

  const webmailLink = useMemo(() => {
    const domain = (email.split("@")[1] || "").toLowerCase();
    if (domain.includes("gmail.")) return "https://mail.google.com/";
    if (domain.includes("outlook.") || domain.includes("live.") || domain.includes("hotmail.")) return "https://outlook.live.com/mail/";
    if (domain.includes("yahoo.")) return "https://mail.yahoo.com/";
    if (domain.includes("proton.")) return "https://mail.proton.me/";
    return "";
  }, [email]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={700}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Forgot your password?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email and we'll send you a link to reset your password.
              </Typography>

              {message && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} role="status" aria-live="polite">
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  id="email"
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!isValidEmail || isSubmitting || isCooldown}
                  startIcon={<SendIcon />}
                  sx={{
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    },
                  }}
                >
                  {isCooldown ? `Resend available in ${secondsLeft}s` : 'Send reset link'}
                </Button>
              </Box>

              {webmailLink && message && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Quick open: <a href={webmailLink} target="_blank" rel="noopener noreferrer">open your inbox</a>
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tip: Check your spam or junk folder if the email doesn’t arrive within a few minutes.
              </Typography>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Button variant="text" startIcon={<ArrowBack />} onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                  Back to sign in
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;


